import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { api } from "@/lib/api";

const STATUS_TONE = {
  assigned: "bg-slate-100 text-slate-600", in_progress: "bg-blue-100 text-blue-700",
  staff_interview: "bg-indigo-100 text-indigo-700", staff_data_entry: "bg-indigo-100 text-indigo-700",
  waiting_on_client: "bg-amber-100 text-amber-700", submitted: "bg-violet-100 text-violet-700",
  complete: "bg-emerald-100 text-emerald-700", reopened: "bg-orange-100 text-orange-700",
};
const FILTERS = [
  ["all", "All"], ["submitted", "Submitted"], ["in_progress", "In Progress"],
  ["waiting_on_client", "Waiting on Client"], ["assigned", "Not Started"], ["complete", "Complete"],
];

export default function StaffOrganizers() {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");

  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (status !== "all") params.set("status", status);
    if (q) params.set("q", q);
    api.get(`/organizers?${params}`).then((r) => setRows(r.data.items)).catch(() => setRows([]));
  }, [status, q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-5" data-testid="staff-organizers-page">
      <div>
        <p className="text-xs text-slate-400 uppercase tracking-wide">Client Success</p>
        <h1 className="text-2xl font-bold text-slate-900">Organizers</h1>
        <p className="text-sm text-slate-500 mt-0.5">Work every client organizer in one place — complete on behalf of clients, review, and approve.</p>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-3 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Icons.Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="org-search" placeholder="Search client or organizer…"
            className="w-full bg-slate-50 border border-slate-200 rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200" />
        </div>
        {FILTERS.map(([k, lbl]) => (
          <button key={k} onClick={() => setStatus(k)} data-testid={`org-filter-${k}`}
            className={`text-sm px-3 py-1.5 rounded-lg border ${status === k ? "border-blue-500 bg-blue-50 text-blue-700" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>{lbl}</button>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
        <table className="w-full text-sm" data-testid="org-table">
          <thead><tr className="text-left text-xs text-slate-400 border-b border-slate-100">
            <th className="px-5 py-2.5 font-medium">Client</th><th className="px-3 py-2.5 font-medium">Engagement</th>
            <th className="px-3 py-2.5 font-medium">Organizer</th><th className="px-3 py-2.5 font-medium">Method</th>
            <th className="px-3 py-2.5 font-medium">Progress</th><th className="px-3 py-2.5 font-medium">Status</th><th></th>
          </tr></thead>
          <tbody>
            {rows === null ? <tr><td colSpan={7} className="px-5 py-12 text-center"><Icons.Loader2 className="h-5 w-5 animate-spin text-blue-500 inline" /></td></tr> :
            rows.length === 0 ? <tr><td colSpan={7} className="px-5 py-12 text-center text-slate-400">No organizers found.</td></tr> :
            rows.map((o) => (
              <tr key={o.id} onClick={() => navigate(`/client-success/organizers/${o.id}`)} data-testid={`org-row-${o.id}`}
                className="border-b border-slate-50 hover:bg-slate-50 cursor-pointer">
                <td className="px-5 py-3"><div className="font-medium text-slate-800">{o.client_name || "—"}</div><div className="text-xs text-slate-400">{o.client_number}</div></td>
                <td className="px-3 py-3 font-mono text-xs text-slate-600">{o.engagement_number || "—"}</td>
                <td className="px-3 py-3 text-slate-700">{o.name}</td>
                <td className="px-3 py-3 text-xs text-slate-500">{(o.completion_method || "—").replace(/_/g, " ")}</td>
                <td className="px-3 py-3">
                  <div className="flex items-center gap-2"><div className="w-20 h-1.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-blue-600" style={{ width: `${o.progress}%` }} /></div><span className="text-xs text-slate-500">{o.progress}%</span></div>
                  {o.required_remaining > 0 && <span className="text-[10px] text-amber-600">{o.required_remaining} req left</span>}
                </td>
                <td className="px-3 py-3"><span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_TONE[o.status] || "bg-slate-100"}`}>{(o.status || "").replace(/_/g, " ")}</span></td>
                <td className="px-3 py-3 text-right pr-5"><Icons.ChevronRight className="h-4 w-4 text-slate-300 inline" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
